import React, { useState, useEffect } from "react";
import { Editor } from "@tinymce/tinymce-react";
import { updateMergeTagsDisplayName } from "@/Utils/MergeTags";
import { TinyMceOptions } from "@/Contracts/Utils";
import { EditorProps } from "./interface";
import ZMergeTagCascadeDropdown from "../ZMergeTagCascadeDropdown";
import { MergeTagVariable, ZomentumWindow } from "@/Contracts";
import { mergeTagOnSubmit } from "@/Utils/MergeTags";
import { convertTextToMergeTag } from "@/Utils/MergeTags";
import { MergeTagVariableDataType } from "@/Contracts/Documents";
import { MERGE_TAG_START } from "@/Utils/MergeTags";
import ZMessage from "../../ZMessage";
import ZSpin from "../../ZSpin";

declare let window: ZomentumWindow;

const MERGE_TAG_DROPDOWN_MIN_POSITION_FROM_RIGHT = 175;

const ZEditorMergeTag = React.forwardRef((props: EditorProps, ref: any) => {
	const innerRef = ref as React.RefObject<HTMLDivElement>;
	const fontSize = props.fontSize ? props.fontSize : `14px`;
	let content_mce_style = `font-size: ${fontSize} !important;
	font-family: 'Source Sans Pro';
	`;
	if (props.border)
		content_mce_style =
			content_mce_style +
			`border: 1px solid #d8d8d8;
	border-radius: 4px;`;

	if (props.descriptive) {
		content_mce_style = content_mce_style + `min-height: 7rem;`;
	} else {
		content_mce_style = content_mce_style + `min-height: 1rem;`;
	}

	const text = props.value ?? "";

	const editorId = props.id.replace(/\./g, "-").replace(/\s/g, "_");

	const filterMergeTags = (mergeTags: MergeTagVariable[]) => {
		let filterOfMergeTags: MergeTagVariableDataType[] = [];
		switch (props.data_type) {
			case MergeTagVariableDataType.TEXT:
				filterOfMergeTags = [
					MergeTagVariableDataType.TEXT,
					MergeTagVariableDataType.DOUBLE,
					MergeTagVariableDataType.NUMBER
				];
				break;
			case MergeTagVariableDataType.DOUBLE:
				filterOfMergeTags = [
					MergeTagVariableDataType.DOUBLE,
					MergeTagVariableDataType.NUMBER
				];
				break;
			case MergeTagVariableDataType.NUMBER:
				filterOfMergeTags = [MergeTagVariableDataType.NUMBER];
				break;
		}

		if (props.data_type) {
			return mergeTags.filter(
				merge_tag =>
					merge_tag.data_type &&
					filterOfMergeTags.includes(merge_tag.data_type)
			);
		}

		return mergeTags;
	};

	const filteredMergeTags = filterMergeTags(props.MergeTags);

	const [visible, setVisible] = useState(false);
	const [mergeTagDropdownX, setMergeTagDropdownX] = useState<number>(0);
	const [mergeTagDropdownY, setMergeTageDropdownY] = useState<number>(0);
	const [editorLoading, setEditorLoading] = useState<boolean>(
		props.showInitialLoading ?? true
	);

	const mergeTagDropDownOnSubmit = (key: string) => {
		mergeTagOnSubmit(window, filteredMergeTags, key);
		setVisible(false);
	};

	const updateText = () => {
		setVisible(false);
		if (
			window.tinyMCE.activeEditor.selection &&
			editorId === window.tinyMCE.activeEditor.id
		) {
			const currentRange = window.tinyMCE.activeEditor.selection.getRng(
				window.tinyMCE.activeEditor.selection.getNode()
			);

			const editorText =
				currentRange &&
				currentRange.startContainer.textContent.substr(
					0,
					currentRange.startOffset
				);

			if (editorText.endsWith(MERGE_TAG_START)) {
				window.requestAnimationFrame(() => {
					!!filteredMergeTags.length && setVisible(true);
				});
			}
			const caretPosition =
				window.tinyMCE.activeEditor.selection.getBoundingClientRect();
			const editorDimension = window.tinyMCE.activeEditor
				.getContentAreaContainer()
				.getBoundingClientRect();

			if (caretPosition && editorDimension) {
				setMergeTagDropdownX(
					Math.min(
						caretPosition.left - editorDimension.x,
						editorDimension.width -
							MERGE_TAG_DROPDOWN_MIN_POSITION_FROM_RIGHT
					)
				);
				setMergeTageDropdownY(caretPosition.bottom - editorDimension.y);
			}
			const inputElement = window.tinyMCE.get(editorId);
			if (inputElement) {
				// Allowing html for descriptive fields
				if (props.descriptive)
					props.onChange?.(inputElement.getContent());
				const inner_text = inputElement.targetElm.innerText.trim();
				if (props.data_type === MergeTagVariableDataType.TEXT)
					props.onChange?.(inner_text);
				else props.onChange?.(inner_text.trim());
			}
		}
	};

	useEffect(() => {
		if (props.dynamicContent && !props.descriptive && window.tinyMCE) {
			const inputElement = window.tinyMCE.get(editorId);
			const inner_text = inputElement.targetElm.innerText.trim();
			if (inner_text !== props.value) {
				inputElement.setContent(props.value);
			}
		}
	}, [props.value]);

	return (
		<ZSpin
			spinning={editorLoading}
			wrapperClassName={`w-full inline-block ${props.className}`}
		>
			<div className={editorId} ref={innerRef}>
				<Editor
					id={editorId}
					apiKey={process.env.REACT_APP_TINY_MCE}
					disabled={props.disabled}
					initialValue={props.initialValue}
					init={{
						...TinyMceOptions,
						paste_as_text: true,
						toolbar: false,
						inline: true,
						indent: false,
						placeholder: props.placeholder,
						setup: (editor: any) => {
							editor.on("keydown", (event: KeyboardEvent) => {
								if (
									props.data_type &&
									[
										MergeTagVariableDataType.DOUBLE,
										MergeTagVariableDataType.NUMBER
									].includes(props.data_type)
								) {
									// Allow Ctrl+C (or Cmd+C), Ctrl+V (or Cmd+V), Ctrl+A (or Cmd+A)
									if (
										(event.ctrlKey || event.metaKey) &&
										["c", "v", "a"].includes(
											event.key.toLowerCase()
										)
									) {
										return; // Allow copy, paste, select all
									}
									// Allow Action keys
									if (
										[
											"{",
											"}",
											"Backspace",
											"Delete",
											"Tab",
											"ArrowLeft",
											"ArrowRight",
											"Meta",
											"Alt",
											"Shift",
											"Control",
											"Home",
											"End"
										].includes(event.key)
									) {
										return;
									}
									// Allow digits 0-9 and a single decimal point
									if (/^\d+$/.test(event.key)) {
										return;
									}
									//Allow a single decimal point when data type is double
									if (
										props.data_type ===
											MergeTagVariableDataType.DOUBLE &&
										event.key === "."
									) {
										return;
									}

									// Prevent any other keypress
									event.preventDefault();
									ZMessage.error(
										"Invalid entry. Only numeric values is allowed."
									);
								}

								if (
									event.key === "Enter" &&
									!props.descriptive
								) {
									event.preventDefault();
								}
							});

							editor.on("keypress", (event: KeyboardEvent) => {
								if (!props.descriptive) {
									if (
										props.contentSize &&
										window.tinyMCE.get(editorId).targetElm
											.innerText.length >=
											props.contentSize
									) {
										event.preventDefault();
									}
								}
							});
						},
						content_style: `
						.${editorId} .mce-content-body {
							${content_mce_style}
							padding: 0.214rem 0rem;
							${props.editor_css}
							white-space: pre-line;
						}

						.${editorId} .mce-content-body:hover {
							box-shadow: 0px 0px 0px 1px #2f70ff;
						}

						.${editorId} p {
							margin: 0 0.7rem;
							margin-top: 0rem;
							line-height: 25px;
						}
						.merge-tag {
								background: #ffedb4;
								cursor: auto !important;
						}

						.mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {
							margin: 0 0.7rem;
							margin-top: 0rem;
							line-height: 25px;
							color: rgba(0,0,0,0.3);
						  }
							
					`,
						init_instance_callback: (editor: any) => {
							text?.length &&
								editor.setContent(
									updateMergeTagsDisplayName(
										filteredMergeTags,
										convertTextToMergeTag(
											filteredMergeTags,
											text,
											true
										)
									)
								);
						}
					}}
					onEditorChange={updateText}
					onInit={() => {
						setEditorLoading(false);
						props.handleInit?.();
					}}
				/>
				<div
					style={{
						position: "absolute",
						top: mergeTagDropdownY,
						left: mergeTagDropdownX,
						zIndex: 50
					}}
				>
					<ZMergeTagCascadeDropdown
						shouldAllowAddVariable={false}
						mergeTagSystemVariables={filteredMergeTags}
						mergeTagCustomVariables={[]}
						visible={
							visible &&
							editorId === window.tinyMCE.activeEditor.id
						}
						onSubmit={key => {
							setVisible(false);
							window.requestAnimationFrame(() => {
								window.tinyMCE.activeEditor.focus();
								mergeTagDropDownOnSubmit(key);
							});
						}}
						onClose={() => {
							setVisible(false);
							window.requestAnimationFrame(() => {
								window.tinyMCE.activeEditor.focus();
							});
						}}
					/>
				</div>
			</div>
		</ZSpin>
	);
});

export default ZEditorMergeTag;
